import {
  AllMenusQuery,
  AnyOperationId,
  BlogOrganisationTermsQuery,
  CategoryTermsQuery,
  EntityUrlsQuery,
  EventCategoryTermsQuery,
  FieldOfExpertiseTermsQuery,
  IndustryTermsQuery,
  LatestEventsQuery,
  MedicalDomainTermsQuery,
  NewsEventTermsQuery,
  NewsTypeTermsQuery,
  OperationExecutorsProvider,
  OperationResult,
  OperationVariables,
  OverlaysQuery,
  ParaworkJobTypeTermsQuery,
  SettingsCallToActionQuery,
  SettingsNewsroomMediaRequestDetailsQuery,
  SettingsNewsroomUrlQuery,
  SettingsStorifyMeQuery,
  SettingsZsmFormsQuery,
  SiteMetadataQuery,
  Url,
} from '@custom/schema';
import { Layout as UILayout } from '@custom/ui/routes/Layout';
import React, { ComponentProps, PropsWithChildren } from 'react';
import { useQueryClient } from 'react-query';

import {
  useBlogOrganisationTermsQuery,
  useCategoryTermsQuery,
  useEventCategoryTermsQuery,
  useFieldOfExpertiseTermsQuery,
  useIndustryTermsQuery,
  useLatestEventsQuery,
  useMedicalDomainTermsQuery,
  useNewsEventsTermsQuery,
  useNewsTypesTermsQuery,
  useOverlaysQuery,
  useParaworkJobTypeTermsQuery,
  useSiteMenusQuery,
  useSiteMetadataQuery,
} from '../hooks';
import { useSettingsCallToActionQuery } from '../hooks/useSettingsCallToActionQuery';
import { useSettingsNewsroomMediaRequestDetailsQuery } from '../hooks/useSettingsNewsroomMediaRequestDetailsQuery';
import { useSettingsNewsroomUrlQuery } from '../hooks/useSettingsNewsroomUrl';
import { useSettingsStorifyMeQuery } from '../hooks/useSettingsStorifyMeQuery';
import { useSettingsZSMFormsQuery } from '../hooks/useSettingsZSMFormsQuery';
import { buildUrl } from '../utils';
import { drupalExecutor } from '../utils/operation';

export function GlobalExecutors({ children }: PropsWithChildren) {
  const menus = useSiteMenusQuery();
  const overlays = useOverlaysQuery();
  const callToAction = useSettingsCallToActionQuery();
  const queryClient = useQueryClient();

  return (
    <OperationExecutorsProvider
      executors={[
        {
          executor: drupalExecutor,
        },
        {
          id: AllMenusQuery,
          executor: menus,
        },
        {
          id: OverlaysQuery,
          executor: overlays,
        },
        {
          id: SettingsCallToActionQuery,
          executor: callToAction,
        },
        {
          id: IndustryTermsQuery,
          executor: useIndustryTermsQuery(),
        },
        {
          id: SiteMetadataQuery,
          executor: useSiteMetadataQuery(),
        },
        {
          id: NewsTypeTermsQuery,
          executor: useNewsTypesTermsQuery(),
        },
        {
          id: FieldOfExpertiseTermsQuery,
          executor: useFieldOfExpertiseTermsQuery(),
        },
        {
          id: BlogOrganisationTermsQuery,
          executor: useBlogOrganisationTermsQuery(),
        },
        {
          id: MedicalDomainTermsQuery,
          executor: useMedicalDomainTermsQuery(),
        },
        {
          id: EventCategoryTermsQuery,
          executor: useEventCategoryTermsQuery(),
        },
        {
          id: SettingsNewsroomMediaRequestDetailsQuery,
          executor: useSettingsNewsroomMediaRequestDetailsQuery(),
        },
        {
          id: SettingsCallToActionQuery,
          executor: useSettingsCallToActionQuery(),
        },
        {
          id: CategoryTermsQuery,
          executor: useCategoryTermsQuery(),
        },
        {
          id: LatestEventsQuery,
          executor: useLatestEventsQuery(),
        },
        {
          id: ParaworkJobTypeTermsQuery,
          executor: useParaworkJobTypeTermsQuery(),
        },
        {
          id: SettingsStorifyMeQuery,
          executor: useSettingsStorifyMeQuery(),
        },
        {
          id: SettingsZsmFormsQuery,
          executor: useSettingsZSMFormsQuery(),
        },
        {
          id: SettingsCallToActionQuery,
          executor: useSettingsCallToActionQuery(),
        },
        {
          id: SettingsNewsroomUrlQuery,
          executor: useSettingsNewsroomUrlQuery(),
        },
        {
          id: NewsEventTermsQuery,
          executor: useNewsEventsTermsQuery(),
        },
        {
          id: EntityUrlsQuery,
          executor: async (
            id: AnyOperationId,
            args: OperationVariables<typeof EntityUrlsQuery>,
          ): Promise<OperationResult<typeof EntityUrlsQuery>> => {
            const { language, nodeId, path } = args;

            const isQueryEnabled: boolean =
              typeof window !== 'undefined' &&
              document.cookie.match(/has_backend_credentials=1/i) !== null &&
              (path !== undefined || nodeId !== undefined);

            if (!isQueryEnabled) {
              return {};
            }

            const backendUrl =
              process.env.GATSBY_BACKEND_BASE_URL || 'http://localhost:8888';

            const url = buildUrl(
              [backendUrl, language, 'rest/entity_links'],
              nodeId
                ? {
                    entity_type: 'node',
                    entity_id: nodeId,
                  }
                : {
                    path,
                  },
            );

            const result = await queryClient.fetchQuery<{
              'delete-form'?: Url;
              'edit-form'?: Url;
              'drupal:content-translation-overview'?: Url;
            }>({
              queryKey: [backendUrl, 'entity-urls', language, nodeId, path],
              queryFn: async () =>
                (
                  await fetch(url, {
                    credentials: 'include',
                    headers: { Accept: 'application/json' },
                  })
                ).json(),
              cacheTime: 60 * 60 * 24 * 1000, //1 day
              staleTime: 60 * 60 * 24 * 1000, //1 day
              retry: false,
            });
            return {
              entityUrls: {
                editForm: result['edit-form'],
                deleteForm: result['delete-form'],
                translations: result['drupal:content-translation-overview'],
              },
            };
          },
        },
      ]}
    >
      {children}
    </OperationExecutorsProvider>
  );
}

export const Layout: React.FC<ComponentProps<typeof UILayout>> = (props) => {
  return (
    <GlobalExecutors>
      <UILayout {...props} />
    </GlobalExecutors>
  );
};
